import { Box } from '@chakra-ui/react';
import VFormFieldHorizontal from '@voly/ui-components/src/v2/VFormFieldHorizontal';
import VStack from '@voly/ui-components/src/v2/VStack.jsx';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VIconButton from '@voly/ui-components/src/v2/VIconButton.jsx';
import VInput from '@voly/ui-components/src/v2/VInput.jsx';
import VTooltip from '@voly/ui-components/src/v2/VTooltip';
import { ChangeEvent, KeyboardEvent, ReactElement, useId, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { translate } from 'src/app/project/features/translate/translate';
import { getDecimalPlacesPlaceholder } from 'src/app/project/modules/custom-fields/custom-field-numbers/utils/getDecimalPlacesPlaceholder';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { TPointCustomFieldSubfield } from 'src/app/project/view-models/custom-field-response-model';
import PointFieldName from './PointFieldName';
import { parseNumericValue } from '../../../utils/numericValue';
import UnitNumberInput from '../UnitNumberInput';
import { getNumericFormat, isNumericFieldLocked } from './numericFieldFormat';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';

const MIN_VALUE = Number.MIN_SAFE_INTEGER;
const MISSING_VALUE = 0;
const INPUT_SIZE = 'sm';

type TNewSubValue = {
  value: number | null;
  description: string;
};

const EMPTY_NEW_SUB_VALUE: TNewSubValue = { value: null, description: '' };

const ADD_LABEL_KEY: Partial<Record<ECustomFieldType, string>> = {
  [ECustomFieldType.COST]: 'add_cost',
  [ECustomFieldType.PERCENTAGE]: 'add_percentage',
  [ECustomFieldType.NUMBERS]: 'add_numbers',
};

type TDraftSubValue = Omit<TPointCustomFieldSubfield, 'value'> & { value: number | null };

const toSaved = ({ id, value, description }: TDraftSubValue): TPointCustomFieldSubfield => ({
  ...(id === undefined ? {} : { id }),
  value: value ?? MISSING_VALUE,
  description,
});

const rowsKey = (rows: TDraftSubValue[]): string => JSON.stringify(rows.map(toSaved));

const toNullable = (valueAsNumber: number): number | null =>
  Number.isFinite(valueAsNumber) ? valueAsNumber : null;

interface PointFieldNumericSubValuesProps {
  field: TCustomField;
  value: string;
  subValues?: TPointCustomFieldSubfield[];
  readOnly: boolean;
  onSaveSubValues?: (subValues: TPointCustomFieldSubfield[]) => void;
}

const PointFieldNumericSubValues = ({
  field,
  value,
  subValues = [],
  readOnly,
  onSaveSubValues = () => undefined,
}: PointFieldNumericSubValuesProps): ReactElement => {
  const inputIdPrefix = useId();
  const savedKey = rowsKey(subValues);
  const [seenKey, setSeenKey] = useState(savedKey);
  const [rows, setRows] = useState<TDraftSubValue[]>(subValues);
  const [newRow, setNewRow] = useState<TNewSubValue | null>(null);
  const [expanded, setExpanded] = useState(false);
  const locked = isNumericFieldLocked(field);
  const canEdit = !readOnly && !locked;
  const { unit, decimalPlaces, useGrouping } = getNumericFormat(field);

  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setRows(subValues);
  }

  const numberInputProps = {
    min: MIN_VALUE,
    size: INPUT_SIZE,
    textAlign: 'end',
    placeholder: locked ? '' : getDecimalPlacesPlaceholder(decimalPlaces),
    formatOptions: {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      useGrouping,
    },
  } as const;

  const save = (next: TDraftSubValue[]): void => {
    setRows(next);
    if (rowsKey(next) === savedKey) {
      return;
    }
    onSaveSubValues(next.map(toSaved));
  };

  const updateRow = (index: number, change: Partial<TDraftSubValue>): void => {
    setRows(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...change } : row)));
  };

  const saveRow = (index: number, change: Partial<TDraftSubValue>): void => {
    save(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...change } : row)));
  };

  const deleteRow = (index: number): void => {
    save(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const saveNewRow = (): void => {
    if (newRow === null || newRow.value === null) {
      return;
    }
    save([...rows, { value: newRow.value, description: newRow.description }]);
    setNewRow(null);
  };

  const addRow = (): void => {
    setNewRow(EMPTY_NEW_SUB_VALUE);
    setExpanded(true);
  };

  const blurOnEnter = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  return (
    <VFormFieldHorizontal
      data-testid="point-custom-field-row"
      size="sm"
      label={<PointFieldName field={field} />}
      helperText={field.description}
      errorText={undefined}
      invalid={undefined}
      readOnly={readOnly}
      disabled={locked}
    >
      <VTooltip content={translate('locked_by_admin')} disabled={!locked}>
        <Box data-testid="point-custom-field-control">
          <VStack gap={2}>
            <VStackRow gap={2}>
              <Box flex="1">
                <UnitNumberInput
                  {...numberInputProps}
                  value={parseNumericValue(value)}
                  unit={unit}
                  variant="subtle"
                  disabled
                  locked={locked}
                  onValueChange={() => undefined}
                />
              </Box>
              <VButton
                color="tertiary"
                size="small"
                disabled={rows.length === 0}
                onClick={() => setExpanded(!expanded)}
              >
                {translate(expanded ? 'hide' : 'show')} ({rows.length})
              </VButton>
            </VStackRow>

            {expanded &&
              rows.map((row, index) => (
                <VStackRow key={row.id ?? `new-${index}`} gap={2} data-testid="sub-value-row">
                  <VInput
                    flex="1"
                    size={INPUT_SIZE}
                    value={row.description}
                    placeholder={translate('description')}
                    aria-label={translate('description')}
                    readOnly={!canEdit}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      updateRow(index, { description: event.target.value })
                    }
                    onBlur={() => saveRow(index, {})}
                    onKeyDown={blurOnEnter}
                  />
                  <Box flex="1">
                    <UnitNumberInput
                      {...numberInputProps}
                      ids={{ input: `${inputIdPrefix}-${row.id ?? index}` }}
                      value={row.value}
                      readOnly={!canEdit}
                      calculations
                      onValueChange={(valueAsNumber) =>
                        updateRow(index, { value: toNullable(valueAsNumber) })
                      }
                      onCommit={(settled) => saveRow(index, { value: settled })}
                    />
                  </Box>
                  {canEdit && (
                    <VIconButton
                      aria-label={translate('remove')}
                      size={INPUT_SIZE}
                      variant="ghost"
                      onClick={() => deleteRow(index)}
                    >
                      <VMaterialIcon name="delete" />
                    </VIconButton>
                  )}
                </VStackRow>
              ))}

            {newRow !== null && (
              <VStack gap={2} data-testid="sub-value-row-new">
                <VStackRow gap={2}>
                  <VInput
                    flex="1"
                    size={INPUT_SIZE}
                    value={newRow.description}
                    placeholder={translate('description')}
                    aria-label={translate('description')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setNewRow({ ...newRow, description: event.target.value })
                    }
                  />
                  <Box flex="1">
                    <UnitNumberInput
                      {...numberInputProps}
                      ids={{ input: `${inputIdPrefix}-new` }}
                      value={newRow.value}
                      calculations
                      onValueChange={(valueAsNumber) =>
                        setNewRow({ ...newRow, value: toNullable(valueAsNumber) })
                      }
                      onCommit={(settled) => setNewRow({ ...newRow, value: settled })}
                    />
                  </Box>
                </VStackRow>
                <VStackRow gap={2} justify="flex-end">
                  <VButton color="secondary" size="small" onClick={() => setNewRow(null)}>
                    {translate('cancel')}
                  </VButton>
                  <VButton size="small" disabled={newRow.value === null} onClick={saveNewRow}>
                    {translate('save')}
                  </VButton>
                </VStackRow>
              </VStack>
            )}

            {canEdit && (
              <Box>
                <VButton
                  color="tertiary"
                  size="small"
                  prefixIcon={MdAdd}
                  disabled={newRow !== null}
                  onClick={addRow}
                >
                  {translate(ADD_LABEL_KEY[field.type] ?? 'add_numbers')}
                </VButton>
              </Box>
            )}
          </VStack>
        </Box>
      </VTooltip>
    </VFormFieldHorizontal>
  );
};

export default PointFieldNumericSubValues;
